import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumVehicleProfile } from "./enums/enumVehicleProfile.generated";

// Model Objects

export class VehicleReadOnly extends ModelObject<VehicleReadOnly> {

    private _customEndLatitude: number | null = null;
    /**
    * Custom End Point Latitude
    */
    public get customEndLatitude(): number | null { return this._customEndLatitude }
    public set customEndLatitude(value: number | null) {
      this._customEndLatitude = value === undefined ? null : value;
    }
    private _customEndLongitude: number | null = null;
    /**
    * Custom End Point Longitude
    */
    public get customEndLongitude(): number | null { return this._customEndLongitude }
    public set customEndLongitude(value: number | null) {
      this._customEndLongitude = value === undefined ? null : value;
    }
    private _customStartLatitude: number | null = null;
    /**
    * Custom Start Point Latitude
    */
    public get customStartLatitude(): number | null { return this._customStartLatitude }
    public set customStartLatitude(value: number | null) {
      this._customStartLatitude = value === undefined ? null : value;
    }
    private _customStartLongitude: number | null = null;
    /**
    * Custom Start Point Longitude
    */
    public get customStartLongitude(): number | null { return this._customStartLongitude }
    public set customStartLongitude(value: number | null) {
      this._customStartLongitude = value === undefined ? null : value;
    }
    /**
    * Timestamp at which the vehicle was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.customEndLatitude = jsonSerializedObject.customEndLatitude == null ? null : jsonSerializedObject.customEndLatitude;
          this.customEndLongitude = jsonSerializedObject.customEndLongitude == null ? null : jsonSerializedObject.customEndLongitude;
          this.customStartLatitude = jsonSerializedObject.customStartLatitude == null ? null : jsonSerializedObject.customStartLatitude;
          this.customStartLongitude = jsonSerializedObject.customStartLongitude == null ? null : jsonSerializedObject.customStartLongitude;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.customEndLatitude = this.customEndLatitude == null ? null : this.customEndLatitude;
        toRet.customEndLongitude = this.customEndLongitude == null ? null : this.customEndLongitude;
        toRet.customStartLatitude = this.customStartLatitude == null ? null : this.customStartLatitude;
        toRet.customStartLongitude = this.customStartLongitude == null ? null : this.customStartLongitude;
        toRet.createdAt = this.createdAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class Vehicle extends ModelObjectWithDirtyCheck<Vehicle> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Colour
    * Not Nullable
    * Max Length : 1000
    */
    public colour: string = "";
    /**
    * Custom End Address
    * Not Nullable
    * Max Length : 1000
    */
    public customEndAddressLine1: string = "";
    /**
    * Custom End Address
    * Not Nullable
    * Max Length : 1000
    */
    public customEndAddressLine2: string = "";
    /**
    * Custom End Address
    * Not Nullable
    * Max Length : 1000
    */
    public customEndAddressLine3: string = "";
    /**
    * Custom End Address
    * Not Nullable
    * Max Length : 1000
    */
    public customEndAddressLine4: string = "";
    /**
    * Custom End Address
    * Not Nullable
    * Max Length : 1000
    */
    public customEndAddressLine5: string = "";
    /**
    * Custom End Address
    * Not Nullable
    * Max Length : 1000
    */
    public customEndAddressLine6: string = "";
    /**
    * Custom End Address
    * Not Nullable
    * Max Length : 1000
    */
    public customEndAddressPostcode: string = "";
    /**
    * Custom Start Address
    * Not Nullable
    * Max Length : 1000
    */
    public customStartAddressLine1: string = "";
    /**
    * Custom Start Address
    * Not Nullable
    * Max Length : 1000
    */
    public customStartAddressLine2: string = "";
    /**
    * Custom Start Address
    * Not Nullable
    * Max Length : 1000
    */
    public customStartAddressLine3: string = "";
    /**
    * Custom Start Address
    * Not Nullable
    * Max Length : 1000
    */
    public customStartAddressLine4: string = "";
    /**
    * Custom Start Address
    * Not Nullable
    * Max Length : 1000
    */
    public customStartAddressLine5: string = "";
    /**
    * Custom Start Address
    * Not Nullable
    * Max Length : 1000
    */
    public customStartAddressLine6: string = "";
    /**
    * Custom Start Address
    * Not Nullable
    * Max Length : 1000
    */
    public customStartAddressPostcode: string = "";
    /**
    * Has Custom End Address - True if end is not brewery.
    */
    public hasCustomEndAddress: boolean = false;
    /**
    * Has Custom Start Address - True if start is not brewery.
    */
    public hasCustomStartAddress: boolean = false;
    /**
    * Insurance Company Name
    * Not Nullable
    * Max Length : 1000
    */
    public insuranceCompany: string = "";
    private _insuranceExpiry: LocalDate | null = null;
    /**
    * Insurance Expiry Date
    */
    public get insuranceExpiry(): LocalDate | null { return this._insuranceExpiry }
    public set insuranceExpiry(value: LocalDate | null) {
      this._insuranceExpiry = value === undefined ? null : value;
    }
    /**
    * Insurance Policy Number
    * Not Nullable
    * Max Length : 1000
    */
    public insurancePolicyNumber: string = "";
    /**
    * Make
    * Not Nullable
    * Max Length : 1000
    */
    public make: string = "";
    /**
    * Model
    * Not Nullable
    * Max Length : 1000
    */
    public model: string = "";
    private _motExpiry: LocalDate | null = null;
    /**
    * Mot Expiry Date
    */
    public get motExpiry(): LocalDate | null { return this._motExpiry }
    public set motExpiry(value: LocalDate | null) {
      this._motExpiry = value === undefined ? null : value;
    }
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Vehicle Profile - Van/HGV/etc
    */
    public profile: EnumVehicleProfile = EnumVehicleProfile.none;
    /**
    * Registration
    * Not Nullable
    * Max Length : 1000
    */
    public registration: string = "";
    private _serviceDue: LocalDate | null = null;
    /**
    * Service Due Date
    */
    public get serviceDue(): LocalDate | null { return this._serviceDue }
    public set serviceDue(value: LocalDate | null) {
      this._serviceDue = value === undefined ? null : value;
    }
    private _vehicleStartTime: LocalTime | null = null;
    /**
    * Time of Day at which Vehicle typically leaves for deliveries.
    */
    public get vehicleStartTime(): LocalTime | null { return this._vehicleStartTime }
    public set vehicleStartTime(value: LocalTime | null) {
      this._vehicleStartTime = value === undefined ? null : value;
    }
    private _vehicleEndTime: LocalTime | null = null;
    /**
    * Time of Day at which Vehicle typically finishes journeys.
    */
    public get vehicleEndTime(): LocalTime | null { return this._vehicleEndTime }
    public set vehicleEndTime(value: LocalTime | null) {
      this._vehicleEndTime = value === undefined ? null : value;
    }
    /**
    * Weight Capacity kg
    * Not Negative
    * Greater Than : 0
    */
    public weightCapacityKg: number = 0;
    public readOnly: VehicleReadOnly = new VehicleReadOnly();
    public isHidden: boolean = false;
    private _taxDue: LocalDate | null = null;
    /**
    * Tax Due Date
    */
    public get taxDue(): LocalDate | null { return this._taxDue }
    public set taxDue(value: LocalDate | null) {
      this._taxDue = value === undefined ? null : value;
    }
    /**
    * The distribution centre this vehicle typically delivers from.
    */
    public siteId: Guid = Guid.createEmpty();
    /**
    * Used for prioritising vehicle when optimising journeys on a route.0 as default value and 100 as the highest priority.
    * Not Negative
    * Maximum : 100
    */
    public priorityLevel: number = 0;
    /**
    * Colour to display around the system
    * Not Nullable
    * Max Length : 1000
    */
    public displayColour: string = "";
    private _maximumDistanceMetres: number | null = null;
    /**
    * Maximum distance the vehicle can do in metres.
    * Not Negative
    */
    public get maximumDistanceMetres(): number | null { return this._maximumDistanceMetres }
    public set maximumDistanceMetres(value: number | null) {
      this._maximumDistanceMetres = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.colour = jsonSerializedObject.colour;
          this.customEndAddressLine1 = jsonSerializedObject.customEndAddressLine1;
          this.customEndAddressLine2 = jsonSerializedObject.customEndAddressLine2;
          this.customEndAddressLine3 = jsonSerializedObject.customEndAddressLine3;
          this.customEndAddressLine4 = jsonSerializedObject.customEndAddressLine4;
          this.customEndAddressLine5 = jsonSerializedObject.customEndAddressLine5;
          this.customEndAddressLine6 = jsonSerializedObject.customEndAddressLine6;
          this.customEndAddressPostcode = jsonSerializedObject.customEndAddressPostcode;
          this.customStartAddressLine1 = jsonSerializedObject.customStartAddressLine1;
          this.customStartAddressLine2 = jsonSerializedObject.customStartAddressLine2;
          this.customStartAddressLine3 = jsonSerializedObject.customStartAddressLine3;
          this.customStartAddressLine4 = jsonSerializedObject.customStartAddressLine4;
          this.customStartAddressLine5 = jsonSerializedObject.customStartAddressLine5;
          this.customStartAddressLine6 = jsonSerializedObject.customStartAddressLine6;
          this.customStartAddressPostcode = jsonSerializedObject.customStartAddressPostcode;
          this.hasCustomEndAddress = jsonSerializedObject.hasCustomEndAddress;
          this.hasCustomStartAddress = jsonSerializedObject.hasCustomStartAddress;
          this.insuranceCompany = jsonSerializedObject.insuranceCompany;
          this.insuranceExpiry = jsonSerializedObject.insuranceExpiry == null ? null : LocalDate.parse(jsonSerializedObject.insuranceExpiry);
          this.insurancePolicyNumber = jsonSerializedObject.insurancePolicyNumber;
          this.make = jsonSerializedObject.make;
          this.model = jsonSerializedObject.model;
          this.motExpiry = jsonSerializedObject.motExpiry == null ? null : LocalDate.parse(jsonSerializedObject.motExpiry);
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.profile = EnumVehicleProfile.getByValue(jsonSerializedObject.profile, this.profile);
          this.registration = jsonSerializedObject.registration;
          this.serviceDue = jsonSerializedObject.serviceDue == null ? null : LocalDate.parse(jsonSerializedObject.serviceDue);
          this.vehicleStartTime = jsonSerializedObject.vehicleStartTime == null ? null : LocalTime.parse(jsonSerializedObject.vehicleStartTime);
          this.vehicleEndTime = jsonSerializedObject.vehicleEndTime == null ? null : LocalTime.parse(jsonSerializedObject.vehicleEndTime);
          this.weightCapacityKg = jsonSerializedObject.weightCapacityKg;
          this.readOnly = new VehicleReadOnly(jsonSerializedObject.readOnly);
          this.isHidden = jsonSerializedObject.isHidden;
          this.taxDue = jsonSerializedObject.taxDue == null ? null : LocalDate.parse(jsonSerializedObject.taxDue);
          this.siteId = Guid.parseOrEmpty(jsonSerializedObject.siteId);
          this.priorityLevel = jsonSerializedObject.priorityLevel;
          this.displayColour = jsonSerializedObject.displayColour;
          this.maximumDistanceMetres = jsonSerializedObject.maximumDistanceMetres == null ? null : jsonSerializedObject.maximumDistanceMetres;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.colour = this.colour;
        toRet.customEndAddressLine1 = this.customEndAddressLine1;
        toRet.customEndAddressLine2 = this.customEndAddressLine2;
        toRet.customEndAddressLine3 = this.customEndAddressLine3;
        toRet.customEndAddressLine4 = this.customEndAddressLine4;
        toRet.customEndAddressLine5 = this.customEndAddressLine5;
        toRet.customEndAddressLine6 = this.customEndAddressLine6;
        toRet.customEndAddressPostcode = this.customEndAddressPostcode;
        toRet.customStartAddressLine1 = this.customStartAddressLine1;
        toRet.customStartAddressLine2 = this.customStartAddressLine2;
        toRet.customStartAddressLine3 = this.customStartAddressLine3;
        toRet.customStartAddressLine4 = this.customStartAddressLine4;
        toRet.customStartAddressLine5 = this.customStartAddressLine5;
        toRet.customStartAddressLine6 = this.customStartAddressLine6;
        toRet.customStartAddressPostcode = this.customStartAddressPostcode;
        toRet.hasCustomEndAddress = this.hasCustomEndAddress;
        toRet.hasCustomStartAddress = this.hasCustomStartAddress;
        toRet.insuranceCompany = this.insuranceCompany;
        toRet.insuranceExpiry = this.insuranceExpiry == null ? null : this.insuranceExpiry.toString();
        toRet.insurancePolicyNumber = this.insurancePolicyNumber;
        toRet.make = this.make;
        toRet.model = this.model;
        toRet.motExpiry = this.motExpiry == null ? null : this.motExpiry.toString();
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.profile = this.profile.value;
        toRet.registration = this.registration;
        toRet.serviceDue = this.serviceDue == null ? null : this.serviceDue.toString();
        toRet.vehicleStartTime = this.vehicleStartTime == null ? null : this.vehicleStartTime.toString();
        toRet.vehicleEndTime = this.vehicleEndTime == null ? null : this.vehicleEndTime.toString();
        toRet.weightCapacityKg = this.weightCapacityKg;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.isHidden = this.isHidden;
        toRet.taxDue = this.taxDue == null ? null : this.taxDue.toString();
        toRet.siteId = this.siteId.toString();
        toRet.priorityLevel = this.priorityLevel;
        toRet.displayColour = this.displayColour;
        toRet.maximumDistanceMetres = this.maximumDistanceMetres == null ? null : this.maximumDistanceMetres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "Vehicle.Id cannot be empty"},
            {field: "colour", rule: (v: any) => v == null || v.length <= 1000 || "Colour has too many characters (1000)"},
            {field: "customEndAddressLine1", rule: (v: any) => v == null || v.length <= 1000 || "CustomEndAddressLine1 has too many characters (1000)"},
            {field: "customEndAddressLine2", rule: (v: any) => v == null || v.length <= 1000 || "CustomEndAddressLine2 has too many characters (1000)"},
            {field: "customEndAddressLine3", rule: (v: any) => v == null || v.length <= 1000 || "CustomEndAddressLine3 has too many characters (1000)"},
            {field: "customEndAddressLine4", rule: (v: any) => v == null || v.length <= 1000 || "CustomEndAddressLine4 has too many characters (1000)"},
            {field: "customEndAddressLine5", rule: (v: any) => v == null || v.length <= 1000 || "CustomEndAddressLine5 has too many characters (1000)"},
            {field: "customEndAddressLine6", rule: (v: any) => v == null || v.length <= 1000 || "CustomEndAddressLine6 has too many characters (1000)"},
            {field: "customEndAddressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "CustomEndAddressPostcode has too many characters (1000)"},
            {field: "customStartAddressLine1", rule: (v: any) => v == null || v.length <= 1000 || "CustomStartAddressLine1 has too many characters (1000)"},
            {field: "customStartAddressLine2", rule: (v: any) => v == null || v.length <= 1000 || "CustomStartAddressLine2 has too many characters (1000)"},
            {field: "customStartAddressLine3", rule: (v: any) => v == null || v.length <= 1000 || "CustomStartAddressLine3 has too many characters (1000)"},
            {field: "customStartAddressLine4", rule: (v: any) => v == null || v.length <= 1000 || "CustomStartAddressLine4 has too many characters (1000)"},
            {field: "customStartAddressLine5", rule: (v: any) => v == null || v.length <= 1000 || "CustomStartAddressLine5 has too many characters (1000)"},
            {field: "customStartAddressLine6", rule: (v: any) => v == null || v.length <= 1000 || "CustomStartAddressLine6 has too many characters (1000)"},
            {field: "customStartAddressPostcode", rule: (v: any) => v == null || v.length <= 1000 || "CustomStartAddressPostcode has too many characters (1000)"},
            {field: "insuranceCompany", rule: (v: any) => v == null || v.length <= 1000 || "InsuranceCompany has too many characters (1000)"},
            {field: "insurancePolicyNumber", rule: (v: any) => v == null || v.length <= 1000 || "InsurancePolicyNumber has too many characters (1000)"},
            {field: "make", rule: (v: any) => v == null || v.length <= 1000 || "Make has too many characters (1000)"},
            {field: "model", rule: (v: any) => v == null || v.length <= 1000 || "Model has too many characters (1000)"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "registration", rule: (v: any) => v == null || v.length <= 1000 || "Registration has too many characters (1000)"},
            {field: "weightCapacityKg", rule: (v: any) => v == null || v > 0 || "WeightCapacityKg is not greater than minimum allowed value: 0"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "siteId", rule: (v: any) => !v.isEmpty() || "Vehicle.SiteId cannot be empty"},
            {field: "priorityLevel", rule: (v: any) => v == null || v >= 0 || "PriorityLevel is not greater than minimum allowed value: 0"},
            {field: "priorityLevel", rule: (v: any) => v == null || v <= 100 || "PriorityLevel is above maximum allowed value: 100"},
            {field: "displayColour", rule: (v: any) => v == null || v.length <= 1000 || "DisplayColour has too many characters (1000)"},
    ];
}


export class VehicleService extends ServiceBase {

  public async getAllVehicles(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Vehicle)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.vehicles.map((v: any) => new Vehicle(v));;
    return this.makeJsonRequest("vehicle/v1/getAllVehicles", req, false, extractor, opts);
  };

  public async getVehicle(vehicleId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Vehicle> {
    const req = {
    tenantId:api.tenantId.toString(),
    vehicleId:vehicleId.toString(),
    };
    const extractor = (response:any) => new Vehicle(response.vehicle);;
    return this.makeJsonRequest("vehicle/v1/getVehicle", req, false, extractor, opts);
  };

  public async getVehicles(vehicleIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(Vehicle)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    vehicleIds:vehicleIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vehicles.map((v: any) => new Vehicle(v));;
    return this.makeJsonRequest("vehicle/v1/getVehicles", req, false, extractor, opts);
  };

  public async saveVehicle(vehicle: Vehicle, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vehicle:vehicle.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("vehicle/v1/saveVehicle", req, true, extractor, opts);
  };

  public async setVehicleIsHidden(vehicleId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vehicleId:vehicleId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("vehicle/v1/setVehicleIsHidden", req, true, extractor, opts);
  };
}

export const vehicleService = new VehicleService();